import React, { useMemo, useState } from "react";
import { defaultConfig, computeCtc } from "../../../../lib/indiaPayroll";

const NEON = "#00FF99";

export default function CtcCalculator() {
  const [annualCTC, setAnnualCTC] = useState(1200000); // 12 LPA default
  const [cfg, setCfg] = useState(defaultConfig);

  const out = useMemo(() => computeCtc({ annualCTC: Number(annualCTC || 0), cfg }), [annualCTC, cfg]);

  const set = (path, v) => {
    setCfg(prev => {
      const copy = structuredClone(prev);
      const segs = path.split(".");
      let cur = copy;
      for (let i = 0; i < segs.length - 1; i++) cur = cur[segs[i]];
      cur[segs.at(-1)] = v;
      return copy;
    });
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold" style={{ color: NEON }}>CTC Calculator (India)</h3>
        <span className="text-white/60 text-sm">Admin ▸ Payroll</span>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-white/10 bg-black/30 p-4">
          <p className="font-medium mb-2">CTC & Structure</p>
          <div className="space-y-2 text-sm">
            <label className="flex items-center justify-between gap-3">
              <span>Annual CTC (₹)</span>
              <input type="number" value={annualCTC}
                     onChange={(e)=>setAnnualCTC(e.target.value)}
                     className="w-40 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span>Basic % of CTC</span>
              <input type="number" value={cfg.struct.basicPct}
                     onChange={e=>set("struct.basicPct", Number(e.target.value||0))}
                     className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span>HRA % of Basic</span>
              <input type="number" value={cfg.struct.hraPctOfBasic}
                     onChange={e=>set("struct.hraPctOfBasic", Number(e.target.value||0))}
                     className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span>Include Gratuity (4.81% of Basic)</span>
              <input type="checkbox" checked={cfg.struct.includeGratuity}
                     onChange={e=>set("struct.includeGratuity", e.target.checked)} />
            </label>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/30 p-4">
          <p className="font-medium mb-2">PF / ESI / PT</p>
          <div className="space-y-2 text-sm">
            <label className="flex items-center justify-between gap-3">
              <span>PF enabled</span>
              <input type="checkbox" checked={cfg.pf.enabled}
                     onChange={e=>set("pf.enabled", e.target.checked)} />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span>Apply PF wage ceiling (₹15,000)</span>
              <input type="checkbox" checked={cfg.pf.applyPfWageCeiling}
                     onChange={e=>set("pf.applyPfWageCeiling", e.target.checked)} />
            </label>
            <div className="h-px bg-white/10 my-2" />
            <label className="flex items-center justify-between gap-3">
              <span>ESI enabled</span>
              <input type="checkbox" checked={cfg.esi.enabled}
                     onChange={e=>set("esi.enabled", e.target.checked)} />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span>ESI monthly wage ceiling</span>
              <input type="number" value={cfg.esi.monthlyWageCeiling}
                     onChange={e=>set("esi.monthlyWageCeiling", Number(e.target.value||0))}
                     className="w-28 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
            </label>
            <div className="h-px bg-white/10 my-2" />
            <label className="flex items-center justify-between gap-3">
              <span>Professional Tax</span>
              <select className="w-32 bg-black/40 border border-white/10 rounded-lg px-2 py-1"
                      value={cfg.pt.state}
                      onChange={e=>set("pt.state", e.target.value)}>
                <option value="NONE">None</option>
                <option value="MH">Maharashtra</option>
                <option value="KA">Karnataka</option>
                <option value="MANUAL">Manual</option>
              </select>
            </label>
            {cfg.pt.state === "MANUAL" && (
              <label className="flex items-center justify-between gap-3">
                <span>PT monthly (₹)</span>
                <input type="number" value={cfg.pt.manualMonthly}
                       onChange={e=>set("pt.manualMonthly", Number(e.target.value||0))}
                       className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
              </label>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/30 p-4">
          <p className="font-medium mb-2">Income Tax</p>
          <div className="space-y-2 text-sm">
            <label className="flex items-center justify-between gap-3">
              <span>Regime</span>
              <select className="w-32 bg-black/40 border border-white/10 rounded-lg px-2 py-1"
                      value={cfg.tax.regime}
                      onChange={e=>set("tax.regime", e.target.value)}>
                <option value="NEW">New (115BAC)</option>
                <option value="OLD">Old</option>
              </select>
            </label>
            <label className="flex items-center justify-between gap-3">
              <span>Standard deduction (₹)</span>
              <input type="number" value={cfg.tax.stdDeduction}
                     onChange={e=>set("tax.stdDeduction", Number(e.target.value||0))}
                     className="w-28 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
            </label>
            {cfg.tax.regime === "OLD" && (
              <label className="flex items-center justify-between gap-3">
                <span>Other deductions (Old) ₹</span>
                <input type="number" value={cfg.tax.extraDeductionsOld}
                       onChange={e=>set("tax.extraDeductionsOld", Number(e.target.value||0))}
                       className="w-28 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
              </label>
            )}
            <label className="flex items-center justify-between gap-3">
              <span>87A rebate threshold (₹)</span>
              <input type="number"
                     value={cfg.tax.regime === "NEW" ? cfg.tax.rebate87A_new_threshold : cfg.tax.rebate87A_old_threshold}
                     onChange={e => cfg.tax.regime === "NEW"
                       ? set("tax.rebate87A_new_threshold", Number(e.target.value||0))
                       : set("tax.rebate87A_old_threshold", Number(e.target.value||0))}
                     className="w-32 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span>Cess</span>
              <input type="number" value={cfg.tax.cess}
                     step="0.01"
                     onChange={e=>set("tax.cess", Number(e.target.value||0))}
                     className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-right" />
            </label>
          </div>
        </div>
      </div>

      {/* Output */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <p className="font-semibold mb-2">Monthly Components</p>
          <KeyVal k="Basic" v={out.monthly.basic} />
          <KeyVal k="HRA" v={out.monthly.hra} />
          <KeyVal k="Special" v={out.monthly.special} />
          <KeyVal k="Gratuity" v={out.monthly.gratuity} />
          <div className="h-px bg-white/10 my-2" />
          <KeyVal k="PF (Employee)" v={out.monthly.pf.employee} />
          <KeyVal k="PF (Employer)" v={out.monthly.pf.employer} sub={`EPS ${out.monthly.pf.eps} • EPF Emp ${out.monthly.pf.epfEmployer}`} />
          <KeyVal k="ESI (Employee)" v={out.monthly.esi.employee} sub={out.monthly.esi.eligible ? "Eligible" : "Not eligible"} />
          <KeyVal k="ESI (Employer)" v={out.monthly.esi.employer} />
          <KeyVal k="Professional Tax" v={out.monthly.pt} />
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <p className="font-semibold mb-2">Pay Summary (Monthly)</p>
          <KeyVal k="Gross (cash)" v={out.monthly.grossCash} />
          <KeyVal k="Employee deductions (PF+ESI+PT)" v={out.monthly.grossCash - out.monthly.preTaxTakeHome} />
          <KeyVal k="Pre-tax take-home" v={out.monthly.preTaxTakeHome} />
          <KeyVal k="Less: TDS (monthly avg)" v={out.monthly.tds} />
          <div className="h-px bg-white/10 my-2" />
          <KeyVal k="Net Take-home" v={out.monthly.takeHome} highlight />
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <p className="font-semibold mb-2">Annual (Tax Base)</p>
          <KeyVal k="Annual Gross (cash salary)" v={out.annual.grossCash} />
          <KeyVal k="Estimated Annual Tax (incl. cess)" v={out.annual.tax} />
        </div>
      </div>

      <p className="mt-3 text-xs text-white/50">
        Notes: PF/EPS as per EPFO; ESI as per ESIC; New regime slabs & 87A per CBDT. Override values if your policy differs.
      </p>
    </div>
  );
}

function KeyVal({ k, v, sub, highlight }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="text-white/80">{k}{sub && <div className="text-white/50 text-[11px]">{sub}</div>}</div>
      <div className={`font-semibold ${highlight ? "text-[${NEON}]" : ""}`}>₹ {Number(v || 0).toLocaleString("en-IN")}</div>
    </div>
  );
}
